import * as React from "react";
import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import * as _ from "lodash";

import { SearchInput } from "@similarweb/ui-components";

import { ExcelButton } from "app/components/React/ExcelButton/ExcelButton";
import { SWReactTable } from "app/components/React/Table/SWReactTable";
import { i18nFilter } from "app/filters/i18nFilter";
import { countryTextByIdFilter } from "app/filters/ngFilters";
import { geographyTableColumnsConfigGen } from "app/pages/website-analysis/geography/GeographyTableColumnConfig";
import { ButtonContainer, TableTopContainer } from "app/pages/website-analysis/geography/StyledComponents";

import { TableWrapper } from "../../conversion/conversionTableContainer/StyledComponents";

interface IGeographyTableProps {
    tableData: any;
    isLoading?: boolean;
    tableExcelUrl?: string;
    isCompare?: boolean;
    isRegionalsEnabled?: boolean;
}

export const GeographyTable = (props: IGeographyTableProps) => {
    const { params } = useCurrentStateAndParams();
    const isLast28Days = params.duration === "28d";
    const { isRegionalsEnabled, isCompare } = props;
    const [searchString, setSearchString] = useState("");
    const [sortedColumn, setSortedColumn] = useState<{
        field: string;
        sortDirection: "desc" | "asc";
    }>({
        field: "Share",
        sortDirection: "desc",
    });

    const onSearch = React.useMemo(
        () => (val: string) => {
            setSearchString(val.trim().toLowerCase());
        },
        [],
    );

    const onSort = React.useMemo(
        () =>
            ({ field, sortDirection }) => {
                setSortedColumn({
                    field,
                    sortDirection,
                });
            },
        [],
    );

    const { isLoading, tableExcelUrl, tableData } = props;
    const filteredData = React.useMemo(() => {
        if (isLoading || !tableData) {
            return [];
        }
        let result =
            !isLoading && searchString !== ""
                ? _.filter(tableData.Data, (dataItem: any) => {
                      return countryTextByIdFilter()(dataItem.Country).toLowerCase().indexOf(searchString) !== -1;
                  })
                : tableData.Data;
        result = orderByWithNullsAtEnd(result, sortedColumn.field, sortedColumn.sortDirection);
        return result;
    }, [isLoading, tableData, searchString, sortedColumn.field, sortedColumn.sortDirection]);

    function orderByWithNullsAtEnd(collection: any[], field: string, sortDirection: "desc" | "asc") {
        if (field === "Country") {
            collection = collection.map((item) => {
                return { ...item, CountryName: countryTextByIdFilter()(item.Country) };
            });
        }
        const partition = _.partition(collection, (x) => !!_.get(x, field, null));
        return _.concat(
            _.orderBy(partition[0], field === "Country" ? "CountryName" : field, sortDirection),
            partition[1],
        );
    }

    const tableColumns = React.useMemo(() => {
        return geographyTableColumnsConfigGen(
            sortedColumn.field,
            sortedColumn.sortDirection,
            isCompare,
            isRegionalsEnabled,
            isLast28Days,
        );
    }, [isRegionalsEnabled, isCompare, sortedColumn.field, sortedColumn.sortDirection, isLast28Days, params.isWWW]);

    const tableProps = React.useMemo(() => {
        return {
            onSort,
            isLoading,
            tableData: {
                Data: filteredData,
                TotalCount: filteredData?.length,
            },
            tableColumns,
        };
    }, [isLoading, tableColumns, filteredData]);
    return (
        <TableWrapper loading={isLoading}>
            <TableTopContainer>
                <SearchInput
                    defaultValue={searchString}
                    debounce={100}
                    onChange={onSearch}
                    placeholder={i18nFilter()("segment.analysis.geography.table.search.placeholder")}
                    disableClear={true}
                />
                {tableExcelUrl ? (
                    <ButtonContainer>
                        <ExcelButton
                            url={tableExcelUrl}
                            trackName="website analysis geography"
                            className={"excel-button-wrapper"}
                        />
                    </ButtonContainer>
                ) : null}
            </TableTopContainer>
            <SWReactTable {...tableProps} />
        </TableWrapper>
    );
};
